import { useEffect, useMemo, useCallback, useRef } from "react";
import { useGetRole } from "../getRole";
import { useVisitState } from "./useVisitState";
import { useMidnightReset } from "./useMidnightReset";
import { useThrottledActivity } from "./useThrottledActivity";
import { useClickTracking } from "./useClickTracking";
import { useRouteTracking } from "./useRouteTracking";
import AnaliticsService from "../../../services/analitics";
import isMobile from "is-mobile";

const VISIT_LIFETIME_MS = 60 * 60 * 1000; // 1 hour
const THROTTLE_MS = 10000; // 10 seconds

export const useVisitTracker = () => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, isAdmin, data] = useGetRole();

  const shouldTrackVisit = useMemo(() => !!data && !isAdmin, [data, isAdmin]);

  const visitState = useVisitState(VISIT_LIFETIME_MS);

  const visitStateRef = useRef(visitState);
  useEffect(() => {
    visitStateRef.current = visitState;
  }, [visitState]);

  const createNewVisit = useCallback(async () => {
    const state = visitStateRef.current;
    const newState = state.createNewState();

    try {
      await AnaliticsService.createVisit({
        visitId: newState.visitId,
        deviceType: isMobile() ? 1 : 2,
      });
    } catch {}

    return newState;
  }, []);

  const handleActivity = useCallback(async () => {
    const state = visitStateRef.current;
    const currentState = state.getState();

    if (!currentState || state.isExpired()) {
      await createNewVisit();
      return;
    }

    state.updateActivity();
  }, [createNewVisit]);

  useEffect(() => {
    if (!shouldTrackVisit) {
      return;
    }

    const initialize = async () => {
      const existingState = visitStateRef.current.load();

      if (!existingState) {
        await createNewVisit();
      }
    };

    initialize();
  }, [shouldTrackVisit, createNewVisit]);

  // Reset visit at midnight
  useMidnightReset(createNewVisit, shouldTrackVisit);

  const throttle = useThrottledActivity(
    handleActivity,
    THROTTLE_MS,
    shouldTrackVisit,
  );

  useClickTracking(throttle.trigger, shouldTrackVisit);
  useRouteTracking(throttle.trigger, shouldTrackVisit);

  return {
    getVisitState: visitState.getState,
  };
};
